import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ActionButton from 'components/Shared/Buttons/ActionButton';
import { vehicleActionStyles } from './styles';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { USER_TYPE_WAREHOUSE, USER_TYPE_ADMIN } from 'types/auth/types';
import CircularProgressWithLabel from 'components/Shared/Progress/CircularProgressWithLabel';
import { useVehicleExport } from './hooks/useVehicleExport';
import { useVehicleImport } from './hooks/useVehicleImport';
import ExportResultDialog from './components/ExportResultDialog';
import ImportResultDialog from './components/ImportResultDialog';
import PaginatedVehicleList from './PaginatedVehicleList';
import { VehiclesSearchContext } from './VehiclesLayout';

export default function VehiclesList() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const user = useSelector((state: RootState) => state.auth.user);
  const { isExporting, exportProgress, exportResult, startExport, clearResult } = useVehicleExport();
  const { isImporting, importProgress, importResult, startImport, clearResult: clearImportResult } = useVehicleImport();
  const isAdmin = user?.user_type === USER_TYPE_ADMIN;
  const { searchText } = useContext(VehiclesSearchContext);
  const isBusy = isExporting || isImporting;
  const [listRefreshKey, setListRefreshKey] = useState(0);

  useEffect(() => {
    if (importResult) {
      setListRefreshKey(key => key + 1);
    }
  }, [importResult]);

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) {
      startImport(file);
    }
  };

  return (
    <>
      {user?.user_type !== USER_TYPE_WAREHOUSE ? (
        <div style={vehicleActionStyles.actionsContainer}>
          {isAdmin ? (
            <>
              <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden onChange={handleFileSelected} />
              <ActionButton type="import" onClick={openFilePicker} disabled={isBusy}>
                Import
              </ActionButton>
              <ActionButton type="export" onClick={startExport} disabled={isBusy}>
                Export
              </ActionButton>
            </>
          ) : null}
          <ActionButton type="add" onClick={() => navigate('/home/vehicles/new')}>
            Add New
          </ActionButton>
        </div>
      ) : null}
      <PaginatedVehicleList searchText={searchText} refreshKey={listRefreshKey} />
      {isExporting ? <CircularProgressWithLabel value={exportProgress} label="Exporting..." /> : null}
      {isImporting ? <CircularProgressWithLabel value={importProgress} label="Importing..." /> : null}
      {exportResult ? (
        <ExportResultDialog
          open
          exportId={exportResult.id}
          status={exportResult.status as 'completed' | 'failed'}
          fileUrl={exportResult.file}
          errorMessage={exportResult.errorMessage}
          onClose={clearResult}
        />
      ) : null}
      {importResult ? (
        <ImportResultDialog
          open
          status={importResult.status as 'completed' | 'failed'}
          errorMessage={importResult.errorMessage}
          resultSummary={importResult.resultSummary}
          onClose={clearImportResult}
        />
      ) : null}
    </>
  );
}
